import type { ReferralAnswerCcReceiver } from '@/hooks/useReferrals';
import { useSorSearch, type SorOrganisation } from '@/hooks/useSorLookup';
import { matchReferralCcReceiver } from '@/lib/referral/epikriseCopyReceiver';
import { receivesEpikriseKind, type EpikriseKind } from '@/lib/referral/epikrise';

export type ReferralCcReceiverLookup =
  | { status: 'none' }
  | { status: 'loading' }
  | { status: 'found'; organisation: SorOrganisation }
  | { status: 'not_found' };

/**
 * DIS01-S 4.29 (G9): the referral's AnswerCCReceiver resolved in SOR, so the
 * epikrise copy goes to it without a manual search. SOR is searched on the
 * identifier and on the name; only an exact identifier match is used.
 */
export function useReferralCcReceiver(
  cc: ReferralAnswerCcReceiver | null | undefined,
  kind: EpikriseKind | null,
): ReferralCcReceiverLookup {
  const identifier = kind ? (cc?.identifier?.trim() ?? '') : '';
  const name = identifier ? (cc?.organisationName ?? cc?.unitName ?? '').trim() : '';
  const byIdentifier = useSorSearch(identifier, null);
  const byName = useSorSearch(name, null);

  if (!identifier || !kind) return { status: 'none' };
  const candidates = [...(byIdentifier.data ?? []), ...(byName.data ?? [])];
  const organisation = matchReferralCcReceiver(cc, candidates, (o) => receivesEpikriseKind(kind, o));
  if (organisation) return { status: 'found', organisation };
  const settled = (q: string, r: { data: unknown; error: unknown }) => q.length < 2 || r.data !== undefined || Boolean(r.error);
  return settled(identifier, byIdentifier) && settled(name, byName) ? { status: 'not_found' } : { status: 'loading' };
}
